import React, { CSSProperties, useEffect, useState, RefObject } from "react";
import { Alert, Card, Space } from "antd";
import {
    createReportFeedback,
    deleteReportFeedback,
    getReportFeedback,
    getStrategies,
    upsertStrategy,
} from "../../../../common/api/ReportClient";
import { handleError } from "../../../../common/ErrorHandling";
import {
    FeedbackCreation,
    NegotiationStrategy,
    POCReport,
    ReportFeedback,
} from "../../../../types/ReportTypes";
import { MultiSectionHtmlSummary } from "./MultiSectionHtmlSummary";
import { NegotiationStrategySummary } from "./NegotiationStrategySummary";
import { Chapter } from "./Outline";
import styles from "./SummaryPanel.module.less";

interface SummaryPanelProps {
    report?: POCReport;
    orderFormId?: number;
    editable?: boolean;
    style?: CSSProperties;
    showTab?: boolean;
    onReportChange?: (report: POCReport) => void;
    selectedSectionByChapter?: Map<Chapter, string>;
    onSectionChange?: (chapter: Chapter, section: string) => void;
    contractSummaryRef?: RefObject<HTMLDivElement>;
    negotiationStrategyRef?: RefObject<HTMLDivElement>;
    topLegalPriorityRef?: RefObject<HTMLDivElement>;
    activeChapters?: string[];
    onActiveChaptersChange?: (chapters: Chapter[]) => void;
}

export const SummaryPanel: React.FC<SummaryPanelProps> = ({
    report = {} as POCReport,
    orderFormId,
    editable,
    style,
    showTab = true,
    onReportChange,
    selectedSectionByChapter,
    onSectionChange,
    contractSummaryRef,
    negotiationStrategyRef,
    topLegalPriorityRef,
    activeChapters,
    onActiveChaptersChange,
}) => {
    const [strategies, setStrategies] = useState([] as NegotiationStrategy[]);
    const [feedback, setFeedback] = useState<ReportFeedback[]>([]);

    const loadStrategies = async () => {
        if (!orderFormId) return;
        try {
            const data = await getStrategies(orderFormId);
            setStrategies(data);
        } catch (error) {
            handleError(error);
        }
    };

    const loadFeedback = async () => {
        if (!report.id) return;
        try {
            const data = await getReportFeedback(report.id);
            setFeedback(data);
        } catch (error) {
            handleError(error);
        }
    };

    const handleStrategyChange = async (strategy: NegotiationStrategy) => {
        if (!orderFormId) return;
        await upsertStrategy({ ...strategy, orderFormId });
        loadStrategies();
    };

    const handleFeedbackAdd = async (data: FeedbackCreation) => {
        if (!report.id) return;
        try {
            await createReportFeedback({
                ...data,
                reportId: report.id,
            });
            await loadFeedback();
        } catch (error) {
            handleError(error);
        }
    };

    const handleFeedbackRemove = async (feedbackId?: number) => {
        if (!report.id) return;
        if (!feedbackId) return;
        try {
            await deleteReportFeedback(report.id, feedbackId);
            await loadFeedback();
        } catch (error) {
            handleError(error);
        }
    };

    useEffect(() => {
        loadStrategies();
    }, [orderFormId]);

    useEffect(() => {
        loadFeedback();
    }, [report.id]);

    const showContent = editable || report.contractSummary || strategies.length > 0;

    return (
        <div style={style}>
            <Space className={styles.summaryContainer} direction="vertical">
                {showContent ? (
                    <>
                        <div ref={contractSummaryRef} className={styles.chapter}>
                            <MultiSectionHtmlSummary
                                title="Contract summary"
                                description="Here’s a summary of your contract."
                                sections={report.contractSummaryV1?.map((s) => ({
                                    ...s,
                                    key: s.key.trim().replace(/^(\d\. *)/, "0$1"),
                                    value: s.value.replace(
                                        /^(<h2[^>]*>\s*<strong[^>]*>.*?)(\d)(\..*?<\/strong>\s*<\/h2>)/,
                                        "$10$2$3"
                                    ),
                                }))}
                                html={report.contractSummary}
                                onUpdate={(value) => {
                                    onReportChange?.({ ...report, contractSummary: value });
                                }}
                                editable={editable}
                                orderFormId={orderFormId}
                                type={showTab ? "tab" : "default"}
                                selectedSection={selectedSectionByChapter?.get(
                                    Chapter.ContractSummary
                                )}
                                onSectionChange={onSectionChange}
                                panelKey={Chapter.ContractSummary}
                                activeKeys={activeChapters}
                                onActiveKeyChange={onActiveChaptersChange}
                                feedback={feedback}
                                onFeedbackAdd={handleFeedbackAdd}
                                onFeedbackRemove={handleFeedbackRemove}
                            />
                        </div>
                        <div ref={negotiationStrategyRef} className={styles.chapter}>
                            <NegotiationStrategySummary
                                title="Negotiation strategy"
                                description="Potential cost-savings strategy checklist."
                                sections={report.negotiationStrategyV1}
                                html={report.negotiationStrategy}
                                onUpdate={(value) => {
                                    onReportChange?.({ ...report, negotiationStrategy: value });
                                }}
                                strategies={strategies}
                                onChange={(data) => handleStrategyChange(data)}
                                totalOpportunity={report}
                                onOpportunityChange={(data) =>
                                    onReportChange?.({ ...report, ...data })
                                }
                                editable={editable}
                                orderFormId={orderFormId}
                                panelKey={Chapter.NegotiationStrategy}
                                activeKeys={activeChapters}
                                onActiveKeyChange={onActiveChaptersChange}
                                feedback={feedback}
                                onFeedbackAdd={handleFeedbackAdd}
                                onFeedbackRemove={handleFeedbackRemove}
                            />
                        </div>
                        <div ref={topLegalPriorityRef} className={styles.chapter}>
                            <MultiSectionHtmlSummary
                                title="Top contractual priorities"
                                description="Critical legal issues and strategies that demand immediate attention."
                                sections={report.topLegalPrioritiesV1}
                                html={report.topLegalPriorities}
                                onUpdate={(value) => {
                                    onReportChange?.({ ...report, topLegalPriorities: value });
                                }}
                                editable={editable}
                                orderFormId={orderFormId}
                                panelKey={Chapter.TopLegalPriorities}
                                activeKeys={activeChapters}
                                onActiveKeyChange={onActiveChaptersChange}
                                feedback={feedback}
                                onFeedbackAdd={handleFeedbackAdd}
                                onFeedbackRemove={handleFeedbackRemove}
                            />
                        </div>
                    </>
                ) : (
                    <>
                        <Card>
                            <Alert
                                type="info"
                                message="The analysis for this vendor is currently underway. Stay tuned for updates!"
                                showIcon={true}
                            />
                        </Card>
                    </>
                )}
            </Space>
        </div>
    );
};
